import { Avatar, Box, Button, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/app/context/AuthContext";
import { replayProductTour } from "@/features/product-tour";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { EditorialPageHeader, EvidenceLabel } from "@/shared/components/ui";
import { layoutTokens } from "@/shared/theme";
import { ProfileOnboarding } from "./ProfileOnboarding";
import { AccountDeletionControl, MembershipLink } from "./components";
import {
  ChessAccountPanel,
  PasswordPanel,
  PersonalDetailsPanel,
  SettingsPanel,
} from "./components/AccountSettingsPanels";
import { useProfile } from "./useProfile";

export const ProfilePage = () => {
  const { profile, notOnboarded, refetch } = useProfile();
  const auth = useAuth();

  if (notOnboarded) {
    return (
      <ProfileOnboarding
        profile={null}
        onProfileCreated={async () => {
          await Promise.all([refetch(), auth.refetch()]);
        }}
      />
    );
  }
  if (!profile || !auth.account) {
    return <ChesslaLoadingMark fillContent page="Profile" label="Reading your account settings" />;
  }
  if (!auth.onboardingCompleted) return <ProfileOnboarding profile={profile} onProfileCreated={refetch} />;

  const refreshAccount = async () => {
    await Promise.all([refetch(), auth.refetch()]);
  };
  const displayName = [auth.account.firstName, auth.account.lastName].filter(Boolean).join(" ") || profile.username;

  return (
    <Box
      data-tour="profile-dashboard"
      sx={{
        px: layoutTokens.workspacePagePadding,
        py: { xs: 3, sm: 5 },
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
      }}
    >
      <EditorialPageHeader
        kicker="Profile"
        title={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              component="span"
              src={profile.chessCom.avatar}
              alt=""
              aria-hidden="true"
              variant="square"
              sx={{
                width: { xs: 48, sm: 80 },
                height: { xs: 48, sm: 80 },
                flexShrink: 0,
                bgcolor: "primary.main",
                color: "background.default",
                fontSize: { xs: "1rem", sm: "1.5rem" },
                fontWeight: 800,
                letterSpacing: 0,
              }}
            >
              {displayName
                .trim()
                .split(/\s+/)
                .map((part) => part[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </Avatar>
            <Box component="span" sx={{ minWidth: 0 }}>
              {displayName.toUpperCase()}
            </Box>
          </Box>
        }
        metadata={
          <>
            <EvidenceLabel kind="source">Private account</EvidenceLabel>
            <Box
              component="a"
              href={`https://www.chess.com/member/${encodeURIComponent(profile.username)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                maxWidth: "100%",
                color: "inherit",
                textDecoration: "none",
                "&:focus-visible": { outline: "2px solid", outlineColor: "secondary.main", outlineOffset: 3 },
              }}
            >
              <EvidenceLabel kind="source">Chess.com · {profile.username}</EvidenceLabel>
            </Box>
          </>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(320px, .85fr)" },
          gap: 2,
          mt: 4,
        }}
      >
        <PersonalDetailsPanel account={auth.account} email={auth.email ?? ""} onSaved={auth.refetch} />
        <PasswordPanel />
        <SettingsPanel
          eyebrow="WORKSPACE PREFERENCES"
          title="Make Chessla yours"
          detail="Board settings, remembered pages, filters, notifications and privacy."
        >
          <Button data-tour="profile-settings" component={RouterLink} to="/settings" variant="outlined">
            OPEN SETTINGS
          </Button>
        </SettingsPanel>
        <ChessAccountPanel profile={profile} onChanged={refreshAccount} />
        <SettingsPanel
          eyebrow="WORKSPACE GUIDE"
          title="Need the map again?"
          detail="Restart the full platform tutorial from Home, then work through each workspace and the review loop."
        >
          <Button
            data-tour="profile-tour-replay"
            variant="outlined"
            onClick={replayProductTour}
            sx={{ borderRadius: 0 }}
          >
            REPLAY PRODUCT TOUR
          </Button>
        </SettingsPanel>
      </Box>

      <Box data-tour="profile-membership" sx={{ mt: layoutTokens.sectionGap }}>
        <MembershipLink />
      </Box>
      <Box
        component="section"
        data-tour="profile-privacy"
        aria-labelledby="account-privacy-heading"
        sx={{ mt: layoutTokens.sectionGap, pt: 3, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Typography
          id="account-privacy-heading"
          component="h2"
          sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 900, letterSpacing: ".12em" }}
        >
          ACCOUNT
        </Typography>
        <AccountDeletionControl />
      </Box>
    </Box>
  );
};
